import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { categories, partners, products } from "../data/products";
import SectionHeading from "../components/SectionHeading";
import { ui, useLang } from "../i18n/i18n";

const HeroStat = ({ label, value }) => (
  <div className="border-t border-[#E5E7EB] pt-4">
    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#6B7280]">{label}</div>
    <div className="mt-1 text-xl md:text-2xl font-semibold text-[#0B1F3A]">{value}</div>
  </div>
);

export default function Home() {
  const { t } = useLang();
  const featured = products.slice(0, 4);
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="hero-bg border-b border-[#E5E7EB]" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3 mb-6 fade-up">
                <span className="mono text-[11px] tracking-[0.2em] uppercase text-[#6B7280]">
                  {t(ui.home.eyebrow)}
                </span>
                <span className="w-8 h-px bg-[#0B1F3A]" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight text-[#0B1F3A] leading-[1.02] fade-up d1">
                {t(ui.home.h1l1)}
              </h1>
              <p className="mt-8 text-[#4B5563] text-base md:text-[18px] max-w-xl leading-relaxed fade-up d2">
                {t(ui.home.heroLead)}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 fade-up d3">
                <Link
                  to="/products"
                  data-testid="hero-cta-products"
                  className="group inline-flex items-center justify-between gap-6 bg-[#0B1F3A] text-white pl-6 pr-4 py-4 text-sm tracking-wide hover:bg-[#122B4E] transition-colors"
                >
                  <span>{t(ui.common.viewCatalog)}</span>
                  <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-cta-about"
                  className="group inline-flex items-center justify-between gap-6 border border-[#0B1F3A] text-[#0B1F3A] pl-6 pr-4 py-4 text-sm tracking-wide hover:bg-[#0B1F3A] hover:text-white transition-colors"
                >
                  <span>{t(ui.common.aboutBrand)}</span>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className="hidden md:block md:col-span-4">
              <div className="grid grid-cols-1 gap-6 pt-12 fade-up d4">
                <HeroStat label={t(ui.home.statLines)} value="20+" />
                <HeroStat label={t(ui.home.statGroups)} value="04" />
                <HeroStat label={t(ui.home.statIndustries)} value={t(ui.home.statIndustriesValue)} />
                <HeroStat label={t(ui.home.statOrigin)} value={t(ui.home.statOriginValue)} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About preview */}
      <section className="border-b border-[#E5E7EB]" data-testid="about-preview">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-5">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {t(ui.home.aboutEyebrow)}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0B1F3A] leading-[1.05]">
                {t(ui.home.aboutTitle)}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7">
              <p className="text-[#4B5563] text-[17px] leading-relaxed">{t(ui.home.aboutP1)}</p>
              <p className="mt-6 text-[#4B5563] text-[17px] leading-relaxed">{t(ui.home.aboutP2)}</p>
              <Link
                to="/about"
                data-testid="about-preview-link"
                className="link-underline mt-10 text-[#0B1F3A] font-medium text-sm tracking-wide"
              >
                {t(ui.common.learnMore)} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product categories */}
      <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="flex items-end justify-between gap-6 mb-14">
            <SectionHeading
              idx="03"
              eyebrow={ui.home.catEyebrow}
              title={ui.home.catTitle}
              description={ui.home.catDesc}
            />
            <Link
              to="/products"
              data-testid="all-products-link"
              className="hidden md:inline-flex link-underline text-[#0B1F3A] text-sm font-medium"
            >
              {t(ui.common.allProducts)} <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E5E7EB] bg-white">
            {categories.slice(0, 4).map((cat, i) => (
              <Link
                key={cat.slug}
                to={`/products?category=${cat.slug}`}
                data-testid={`category-card-${cat.slug}`}
                className={`category-card block p-8 md:p-10 border-[#E5E7EB] ${
                  i % 2 === 0 ? "md:border-r" : ""
                } ${i < 2 ? "border-b" : ""}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                      {cat.code}
                    </div>
                    <h3 className="mt-3 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                      {t(cat.name)}
                    </h3>
                  </div>
                  <ArrowUpRight size={22} className="text-[#0B1F3A] shrink-0 group-hover:rotate-45 transition-transform" />
                </div>
                <p className="mt-5 text-[#4B5563] text-[15px] leading-relaxed max-w-md">
                  {t(cat.short)}
                </p>
                <div className="mt-8 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {t(ui.common.viewProducts)} →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="border-b border-[#E5E7EB]" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="flex items-end justify-between gap-6 mb-10">
            <SectionHeading idx="04" eyebrow={ui.home.featuredEyebrow} title={ui.home.featuredTitle} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {featured.map((p, i) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`featured-product-${p.slug}`}
                className="group bg-white p-6 md:p-8 hover:bg-[#F7F8FA] transition-colors"
              >
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-4 text-xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(p.name)}
                </h3>
                {p.synonym && (
                  <div className="mt-1 text-xs text-[#6B7280] italic">{t(p.synonym)}</div>
                )}
                <p className="mt-4 text-sm text-[#4B5563] leading-relaxed line-clamp-3">
                  {t(p.summary)}
                </p>
                <div className="mt-8 inline-flex items-center gap-2 text-[#0B1F3A] text-xs mono uppercase tracking-[0.2em]">
                  {t(ui.common.view)} <ArrowUpRight size={12} className="group-hover:rotate-45 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="border-b border-[#E5E7EB]" data-testid="partners-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <SectionHeading
            idx="05"
            eyebrow={ui.home.networkEyebrow}
            title={ui.home.networkTitle}
            description={ui.home.networkDesc}
          />

          <div className="mt-14 border-t border-l border-[#E5E7EB] grid grid-cols-2 md:grid-cols-5">
            {partners.map((p) => (
              <div
                key={p.name}
                data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-r border-b border-[#E5E7EB] p-6 md:p-8 flex flex-col items-center justify-center gap-4 min-h-[160px] bg-white hover:bg-[#F7F8FA] transition-colors"
              >
                <div className="h-16 w-full flex items-center justify-center">
                  <img
                    src={p.logo}
                    alt={p.name}
                    loading="lazy"
                    className="max-h-16 max-w-[80%] object-contain"
                  />
                </div>
                <div className="mono text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {p.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
